import { useEffect, useMemo, useState } from "react";
import { apiRequest, assetUrl, formatPrice } from "../api/client";
import Button from "../components/Button";
import EbookCover from "../components/EbookCover";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useCart } from "../context/CartContext";
import { useToast } from "../context/ToastContext";
import fallbackCover from "../assets/Ebook.png";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  city: "",
  address: "",
  payment_method: "paymob",
  notes: "",
};

const transferNumber = "01110243007";

export default function Checkout() {
  const [form, setForm] = useState(initialForm);
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(false);
  const cart = useCart();
  const toast = useToast();

  useEffect(() => {
    if (cart.items.length) return;
    setBooting(true);
    apiRequest("/api/products?featured=true")
      .then((response) => setFeaturedProduct(response.data[0] || null))
      .catch(() => {})
      .finally(() => setBooting(false));
  }, [cart.items.length]);

  const checkoutItems = useMemo(() => {
    if (cart.items.length) return cart.items;
    if (!featuredProduct) return [];
    return [{
      id: featuredProduct.id,
      slug: featuredProduct.slug,
      name: featuredProduct.name,
      image_url: featuredProduct.image_url,
      price: Number(featuredProduct.discount_price || featuredProduct.price),
      original_price: Number(featuredProduct.price),
      quantity: 1,
    }];
  }, [cart.items, featuredProduct]);

  const subtotal = checkoutItems.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1), 0);
  const total = Math.max(0, subtotal - discount);

  function handleChange(event) {
    setForm({ ...form, [event.target.name]: event.target.value });
  }

  async function handlePromo() {
    if (!promoCode) return;
    setStatus({ type: "", message: "" });
    try {
      const response = await apiRequest("/api/promocodes/validate", {
        method: "POST",
        body: { code: promoCode, subtotal },
      });
      setDiscount(response.data.discount);
      setStatus({ type: "success", message: response.message });
    } catch (error) {
      setDiscount(0);
      setStatus({ type: "error", message: error.message });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus({ type: "", message: "" });

    if (!form.name || !form.email || !form.phone || !checkoutItems.length) {
      setStatus({ type: "error", message: "Please complete your details and add a product." });
      return;
    }

    setLoading(true);
    try {
      const endpoint = form.payment_method === "paymob"
        ? "/api/payments/paymob/create-intention"
        : "/api/orders/checkout";
      const response = await apiRequest(endpoint, {
        method: "POST",
        body: {
          customer: {
            name: form.name,
            email: form.email,
            phone: form.phone,
            city: form.city,
            address: form.address,
          },
          items: checkoutItems.map((item) => ({ product_id: item.id, quantity: item.quantity })),
          promo_code: promoCode || null,
          payment_method: form.payment_method,
          notes: form.notes,
        },
      });
      cart.clear();
      const checkoutUrl = response.data.checkout_url || response.data.payment_url;
      if (checkoutUrl) {
        toast?.show("Redirecting to secure payment.");
        window.location.href = checkoutUrl;
      } else {
        setForm(initialForm);
        const orderNumber = response.data.order.order_number;
        setStatus({
          type: "success",
          message: `Order ${orderNumber} was received and is pending payment confirmation. Transfer to ${transferNumber}, then send us the receipt.`,
        });
        toast?.show("Order pending payment confirmation.");
      }
    } catch (error) {
      setStatus({ type: "error", message: error.message });
    } finally {
      setLoading(false);
    }
  }

  if (booting) return <section className="section"><LoadingSpinner label="Preparing checkout" /></section>;

  return (
    <section className="section checkout-page">
      <div className="container checkout-grid">
        <form className="checkout-form" onSubmit={handleSubmit}>
          <span className="eyebrow">Secure checkout</span>
          <h1>Complete Purchase</h1>
          <div className="glass-card form-card">
            <div className="form-heading">
              <h2>Contact Info</h2>
              <span>Step 1 of 3</span>
            </div>
            <div className="form-grid">
              <label>
                <span>Full name</span>
                <input name="name" value={form.name} onChange={handleChange} placeholder="Your name" />
              </label>
              <label>
                <span>Email address</span>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" />
              </label>
            </div>
            <label>
              <span>Phone number</span>
              <input name="phone" value={form.phone} onChange={handleChange} placeholder="+20 100 000 0000" />
            </label>
            <div className="form-grid">
              <label>
                <span>City</span>
                <input name="city" value={form.city} onChange={handleChange} placeholder="Cairo" />
              </label>
              <label>
                <span>Address</span>
                <input name="address" value={form.address} onChange={handleChange} placeholder="Optional address" />
              </label>
            </div>
          </div>

          <div className="glass-card form-card">
            <div className="form-heading">
              <h2>Payment</h2>
              <span>Step 2 of 3</span>
            </div>
            <label>
              <span>Payment method</span>
              <select name="payment_method" value={form.payment_method} onChange={handleChange}>
                <option value="paymob">Visa / card payment by Paymob</option>
                <option value="instapay">InstaPay transfer</option>
                <option value="vodafone_cash">Vodafone Cash transfer</option>
              </select>
            </label>
            {form.payment_method === "paymob" ? (
              <div className="payment-method-card">
                <strong>Paymob secure checkout</strong>
                <p>You will be redirected to Paymob to complete your card payment.</p>
              </div>
            ) : (
              <div className="payment-method-card">
                <strong>{form.payment_method === "instapay" ? "InstaPay transfer" : "Vodafone Cash transfer"}</strong>
                <p>Transfer the total to {transferNumber}. Your order stays pending until the payment receipt is verified.</p>
              </div>
            )}
          </div>

          <div className="glass-card form-card">
            <div className="form-heading">
              <h2>Promo & Notes</h2>
              <span>Step 3 of 3</span>
            </div>
            <div className="promo-field">
              <label>
                <span>Promo code</span>
                <input value={promoCode} onChange={(event) => setPromoCode(event.target.value.toUpperCase())} placeholder="LAUNCH20" />
              </label>
              <Button type="button" variant="secondary" onClick={handlePromo}>Apply</Button>
            </div>
            <label>
              <span>Order notes</span>
              <textarea name="notes" value={form.notes} onChange={handleChange} rows="4" placeholder="Optional notes" />
            </label>
          </div>
          {status.message && <p className={`form-status ${status.type}`}>{status.message}</p>}
          <Button type="submit" disabled={loading}>{loading ? "Processing..." : "Complete Purchase"}</Button>
        </form>

        <aside className="glass-card order-summary">
          <h2>Order Summary</h2>
          {!checkoutItems.length ? (
            <div className="summary-product">
              <EbookCover compact />
              <div>
                <h3>No product selected</h3>
                <p>Visit the shop to choose an eBook.</p>
              </div>
            </div>
          ) : checkoutItems.map((item) => (
            <div className="summary-product" key={item.id}>
              <img className="summary-thumb" src={assetUrl(item.image_url, fallbackCover)} alt={item.name} />
              <div>
                <h3>{item.name}</h3>
                <p>Qty {item.quantity}</p>
                <strong>{formatPrice(item.price)}</strong>
              </div>
            </div>
          ))}
          <div className="summary-lines">
            <div><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></div>
            <div><span>Discount</span><strong>-{formatPrice(discount)}</strong></div>
            <div><span>Tax</span><strong>0 EGP</strong></div>
            <div className="summary-total"><span>Total</span><strong>{formatPrice(total)}</strong></div>
          </div>
          <div className="trust-row">
            <span>Secure</span>
            <span>Encrypted</span>
            <span>Instant access</span>
          </div>
        </aside>
      </div>
    </section>
  );
}
